import { queryClient, WORKSPACE_QUERY_OPTIONS } from "../app/queryClient";
import {
    fetchB2BDataset,
    fetchClientsDatasetPage,
    fetchDashboardHome,
    fetchOrdersDataset,
    fetchSalesAnalytics,
} from "../lib/api";
import { fetchRetailFrequencyReport } from "../lib/api/retailFrequency";
import { fetchExpoPromotionsReport } from "../lib/api/expoPromotions";
import { defaultOrdersPeriod } from "../pages/orders/lib/ordersPeriodDefaults";
import { routePaths } from "./navigation";

const DEFAULT_SALES_PERIOD = "Последни 30 дни";
const PREFETCH_STAGGER_MS = 1_200;
const HOVER_PREFETCH_DELAY_MS = 450;

const warmedChunks = new Set<string>();
const warmedData = new Set<string>();
const hoverTimers = new Map<string, number>();

function warmChunk(path: string, loader: () => Promise<unknown>) {
    if (warmedChunks.has(path)) {
        return;
    }

    warmedChunks.add(path);
    void loader();
}

function prefetchData(queryKey: readonly unknown[], queryFn: () => Promise<unknown>) {
    void queryClient.prefetchQuery({
        queryKey,
        queryFn,
        ...WORKSPACE_QUERY_OPTIONS,
    });
}

interface PrefetchWorkspaceModuleOptions {
    prefetchData?: boolean;
}

function prefetchModuleData(path: string) {
    if (warmedData.has(path)) {
        return;
    }

    warmedData.add(path);

    switch (path) {
        case routePaths.dashboard:
            prefetchData(["dashboard-home"], fetchDashboardHome);
            return;
        case routePaths.clients:
            prefetchData(
                ["clients-dataset", 1, 25, ""],
                () => fetchClientsDatasetPage({ page: 1, perPage: 25 }),
            );
            return;
        case routePaths.orders: {
            const period = defaultOrdersPeriod();
            prefetchData(
                ["orders-dataset", period.dateFrom, period.dateTo, "Всички", "Всички"],
                () => fetchOrdersDataset({
                    dateFrom: period.dateFrom,
                    dateTo: period.dateTo,
                    traderId: "Всички",
                    region: "Всички",
                }),
            );
            return;
        }
        case routePaths.loyalty:
            // Тежък workspace dataset — без prefetch от навигацията.
            return;
        case routePaths.b2b:
            prefetchData(["b2b-dataset", null], () => fetchB2BDataset({}));
            return;
        case routePaths.wholesalePromotions:
            prefetchData(
                ["expo-promotions", "Този месец", "", "", "", ""],
                () => fetchExpoPromotionsReport({ period: "Този месец" }),
            );
            return;
        case routePaths.bi:
            prefetchData(
                [
                    "sales-analytics",
                    DEFAULT_SALES_PERIOD,
                    null,
                    null,
                    "Всички",
                    "Всички",
                    "Всички",
                    "",
                    "",
                    "Всички",
                    "core",
                ],
                () => fetchSalesAnalytics(DEFAULT_SALES_PERIOD, {
                    channel: "Всички",
                    region: "Всички",
                    store: "Всички",
                    client: "",
                    product: "",
                    status: "Всички",
                    sections: "core",
                }),
            );
            return;
        case routePaths.retailCockpit:
            // Тежък report (~100MB+) — всеки таб си зарежда данните; без prefetch.
            return;
        case routePaths.retailFrequency:
            prefetchData(
                ["retail-frequency", DEFAULT_SALES_PERIOD, "", ""],
                () => fetchRetailFrequencyReport({ period: DEFAULT_SALES_PERIOD }),
            );
            return;
        default:
            return;
    }
}

export function prefetchWorkspaceModule(path: string, options?: PrefetchWorkspaceModuleOptions) {
    const shouldPrefetchData = options?.prefetchData ?? true;

    switch (path) {
        case routePaths.dashboard:
            warmChunk(path, () => import("../pages/home"));
            break;
        case routePaths.clients:
            warmChunk(path, () => import("../pages/clients"));
            break;
        case routePaths.orders:
            warmChunk(path, () => import("../pages/orders"));
            break;
        case routePaths.loyalty:
            warmChunk(path, () => import("../pages/loyalty"));
            break;
        case routePaths.b2b:
            warmChunk(path, () => import("../pages/b2b"));
            break;
        case routePaths.wholesalePromotions:
            warmChunk(path, () => import("../pages/b2b/promotions/WholesalePromotionsPage"));
            break;
        case routePaths.wholesaleSuppliers:
            warmChunk(path, () => import("../pages/b2b/suppliers/WholesaleSuppliersPage"));
            break;
        case routePaths.bi:
            warmChunk(path, () => import("../pages/reports"));
            break;
        case routePaths.adminAccess:
            warmChunk(path, () => import("../pages/admin"));
            break;
        case routePaths.retailCockpit:
            warmChunk(path, () => import("../pages/retail/RetailCockpitPage"));
            break;
        case routePaths.onlineCockpit:
            warmChunk(path, () => import("../pages/online/OnlineCockpitPage"));
            break;
        case routePaths.retailFrequency:
            warmChunk(path, () => import("../pages/retail/RetailFrequencyReportPage"));
            break;
        case routePaths.retailDefectsSliven:
            warmChunk(path, () => import("../pages/retail/defectsSliven/RetailDefectsSlivenPage"));
            break;
        default:
            return;
    }

    if (shouldPrefetchData) {
        prefetchModuleData(path);
    }
}

export function scheduleWorkspacePrefetch(path: string) {
    prefetchWorkspaceModule(path, { prefetchData: false });

    const existingTimer = hoverTimers.get(path);

    if (existingTimer) {
        window.clearTimeout(existingTimer);
    }

    const timer = window.setTimeout(() => {
        hoverTimers.delete(path);
        prefetchModuleData(path);
    }, HOVER_PREFETCH_DELAY_MS);

    hoverTimers.set(path, timer);
}

export function prefetchAllWorkspaceModules(paths: string[]) {
    const uniquePaths = [...new Set(paths)];

    uniquePaths.forEach((path, index) => {
        globalThis.setTimeout(
            () => prefetchWorkspaceModule(path),
            index * PREFETCH_STAGGER_MS,
        );
    });
}
